import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useRef} from 'react';
import styled, {css} from 'styled-components';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBox} from '../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBox';
import {TextBox} from '../../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {PopoverCoordinator} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {CreateFormInputDisplay} from '../../../../../components/common/createFormInputs/createFormInputDisplay';
import {renderLocalApplicationServerAuthenticationConfigStrategy} from '../../../../../helpers/localApplicationHelpers';
import {
  LocalApplicationServerAuthenticationConfigModel,
  LocalApplicationServerAuthenticationStrategiesEnum,
  LocalApplicationServerModel,
} from '../../../../../models/api/localApplicationServerModel';

/*
 * Constants.
 */

const dropdownOptions = [
  LocalApplicationServerAuthenticationStrategiesEnum.NONE,
  LocalApplicationServerAuthenticationStrategiesEnum.BASIC,
  LocalApplicationServerAuthenticationStrategiesEnum.BEARER,
  LocalApplicationServerAuthenticationStrategiesEnum.OAUTH2,
];
const dropdownWidth = 165;

/*
 * Props.
 */

interface SettingsDevelopersApplicationCreateEditServerProps {
  className?: string;
  origin: string;
  authConfig: LocalApplicationServerAuthenticationConfigModel;
  isInDialog?: boolean;
  servers: ReadonlyArray<LocalApplicationServerModel>;
  currentServer?: LocalApplicationServerModel;
  requiresHttps?: boolean;
  onAuthConfigChange: HandlerOf<LocalApplicationServerAuthenticationConfigModel>;
  onOriginChange: HandlerOf<string>;
  onFormValidChange: HandlerOf<boolean>;
}

/*
 * Style.
 */

const StyledFormWrapperDiv = styled.div`
  width: 100%;
  padding: 0 1px;
`;

const StyledOriginTextBox = styled(TextBox)`
  max-width: 288px;
`;

interface StyledMessageWrapperDivProps {
  $isInDialog: boolean;
}

const StyledMessageWrapperDiv = styled.div<StyledMessageWrapperDivProps>`
  white-space: nowrap;

  ${(p) =>
    p.$isInDialog &&
    css`
      white-space: normal;
      width: 380px;
    `}
`;

const StyledAuthStrategyDropdownBox = styled(DropdownBox)`
  width: 138px;
`;

/*
 * Component.
 */

export const SettingsDevelopersApplicationCreateEditServer: FC<
  SettingsDevelopersApplicationCreateEditServerProps
> = (props) => {
  const {
    className,
    origin,
    authConfig,
    isInDialog = false,
    servers,
    requiresHttps,
    currentServer,
    onAuthConfigChange,
    onOriginChange,
    onFormValidChange,
  } = props;
  const customAnchorRef = useRef<HTMLDivElement>(null);

  // If we explicitly require https or the auth config is not NONE.
  const isHttpsRequired =
    requiresHttps || authConfig.type !== LocalApplicationServerAuthenticationStrategiesEnum.NONE;
  const isFormValid = computeIsFormValid(origin, authConfig, servers, isHttpsRequired, currentServer);

  useEffect(() => {
    onFormValidChange(isFormValid);
  }, [isFormValid, onFormValidChange]);

  return (
    <StyledFormWrapperDiv className={className}>
      <CreateFormInputDisplay
        isRequired
        title={<Trans id="developers-application-server-modify-origin-label">Origin</Trans>}
        input={
          <StyledOriginTextBox
            value={origin}
            onChange={(value) => {
              try {
                const url = new URL(value);
                // When we have a valid url, we will only return the origin.
                onOriginChange(url.origin);
              } catch {
                onOriginChange(value);
              }
            }}
            isRequired
            hasError={!computeIsOriginValid(origin, servers, isHttpsRequired, currentServer) && origin !== ''}
            errorMessage={
              <StyledMessageWrapperDiv $isInDialog={isInDialog}>
                {renderOriginErrorMessage(origin, servers, isHttpsRequired, currentServer)}
              </StyledMessageWrapperDiv>
            }
            noMargin
            placeholder="https://example.com"
            message={
              <StyledMessageWrapperDiv $isInDialog={isInDialog}>
                <Trans id="developers-application-server-modify-origin-message">
                  The URL origin of the remote server you want to define for this app.
                </Trans>
                {isHttpsRequired && (
                  <>
                    {' '}
                    <Trans id="developers-application-server-modify-origin-https-message">
                      Must be an https endpoint.
                    </Trans>
                  </>
                )}
              </StyledMessageWrapperDiv>
            }
          />
        }
      />
      <CreateFormInputDisplay
        isRequired
        title={
          <Trans id="developers-application-server-modify-auth-strat-label">Authentication strategy</Trans>
        }
        input={
          <PopoverCoordinator
            renderButton={(hasHover) => (
              <StyledAuthStrategyDropdownBox
                ref={customAnchorRef}
                hasHover={hasHover}
                noMargin
                boxStyles={{
                  width: '138px',
                  height: '30px',
                  marginTop: 0,
                }}
                value={renderLocalApplicationServerAuthenticationConfigStrategy(authConfig.type)}
                message={
                  <StyledMessageWrapperDiv $isInDialog={isInDialog}>
                    <Trans id="developers-application-server-modify-auth-strat-message">
                      Select how you want users to authorize this app
                    </Trans>
                  </StyledMessageWrapperDiv>
                }
              />
            )}
            renderModal={(popoverProps) => (
              <SimpleDropdown
                anchor={customAnchorRef}
                onRequestClose={popoverProps.onRequestClose}
                width={dropdownWidth}
              >
                {dropdownOptions.map((option) => (
                  <SimpleDropdownItem
                    key={option}
                    onSelect={() => {
                      // If we select the oauth option, pre-fill or clear the previous values.
                      if (option === LocalApplicationServerAuthenticationStrategiesEnum.OAUTH2) {
                        return onAuthConfigChange({
                          type: option,
                          clientId: '',
                          clientSecret: '',
                          scope: '',
                          authorizationUrl: '',
                          tokenUrl: '',
                        });
                      }
                      return onAuthConfigChange({
                        type: option,
                      });
                    }}
                    isChecked={option === authConfig.type}
                  >
                    {renderLocalApplicationServerAuthenticationConfigStrategy(option)}
                  </SimpleDropdownItem>
                ))}
              </SimpleDropdown>
            )}
          />
        }
      />
      {maybeRenderOAuth2Values(props)}
    </StyledFormWrapperDiv>
  );
};

/*
 * Helpers.
 */

function computeIsFormValid(
  origin: string,
  authConfig: Partial<LocalApplicationServerAuthenticationConfigModel> & {
    type: LocalApplicationServerAuthenticationStrategiesEnum;
  },
  servers: ReadonlyArray<LocalApplicationServerModel>,
  requiresHttps?: boolean,
  currentServer?: LocalApplicationServerModel,
) {
  const isOriginValid = computeIsOriginValid(origin, servers, requiresHttps, currentServer);

  // If we are not doing oauth2 auth, we only need to check the origin
  if (authConfig.type !== LocalApplicationServerAuthenticationStrategiesEnum.OAUTH2) {
    return isOriginValid;
  }

  const {clientId = '', clientSecret = '', scope = '', authorizationUrl = '', tokenUrl = ''} = authConfig;
  return (
    isOriginValid &&
    clientSecret !== '' &&
    clientId !== '' &&
    scope !== '' &&
    isUrlValid(authorizationUrl ?? '') &&
    isUrlValid(tokenUrl ?? '')
  );
}

function computeIsOriginValid(
  origin: string,
  servers: ReadonlyArray<LocalApplicationServerModel>,
  requiresHttps?: boolean,
  currentServer?: LocalApplicationServerModel,
) {
  const isOriginValid = isUrlValid(origin);

  // If the origin is valid but we have a matching origin already setup.
  // If we already have an existing server, check if they match.
  if (isOriginValid && servers.some((s) => s.origin === origin) && currentServer?.origin !== origin) {
    return false;
  }
  if (isOriginValid && requiresHttps && !origin.startsWith('https://')) {
    return false;
  }
  return isOriginValid;
}

function renderOriginErrorMessage(
  origin: string,
  servers: ReadonlyArray<LocalApplicationServerModel>,
  requiresHttps?: boolean,
  currentServer?: LocalApplicationServerModel,
) {
  const isOriginValid = isUrlValid(origin);

  if (servers.some((s) => s.origin === origin) && currentServer?.origin !== origin) {
    return (
      <Trans id="developers-application-server-modify-origin-in-use-error-message">
        This origin is already in use from a different server.
      </Trans>
    );
  }
  if (isOriginValid && requiresHttps && !origin.startsWith('https://')) {
    return (
      <Trans id="developers-application-server-modify-origin-requires-https-error-message">
        The origin must be an https endpoint.
      </Trans>
    );
  }
  return <Trans id="developers-application-server-modify-origin-invalid-error-message">Invalid url.</Trans>;
}

function isUrlValid(url: string) {
  try {
    // eslint-disable-next-line no-new
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

function maybeRenderOAuth2Values(props: SettingsDevelopersApplicationCreateEditServerProps) {
  const {authConfig, isInDialog = false, onAuthConfigChange} = props;

  if (authConfig.type !== LocalApplicationServerAuthenticationStrategiesEnum.OAUTH2) {
    return null;
  }
  return (
    <>
      <CreateFormInputDisplay
        isRequired
        title={<Trans id="developers-application-server-modify-oauth2-client-id-label">Client ID</Trans>}
        input={
          <StyledOriginTextBox
            value={authConfig.clientId ?? ''}
            onChange={(value) =>
              onAuthConfigChange({
                ...authConfig,
                clientId: value,
              })
            }
            isRequired
            noMargin
            message={
              <StyledMessageWrapperDiv $isInDialog={isInDialog}>
                <Trans id="developers-application-server-modify-oauth2-client-id-message">
                  The identifier for this channel when making requests to your server
                </Trans>
              </StyledMessageWrapperDiv>
            }
          />
        }
      />
      <CreateFormInputDisplay
        isRequired
        title={
          <Trans id="developers-application-server-modify-oauth2-client-secret-label">Client secret</Trans>
        }
        input={
          <StyledOriginTextBox
            value={authConfig.clientSecret ?? ''}
            onChange={(value) =>
              onAuthConfigChange({
                ...authConfig,
                clientSecret: value,
              })
            }
            isRequired
            noMargin
            message={
              <StyledMessageWrapperDiv $isInDialog={isInDialog}>
                <Trans id="developers-application-server-modify-oauth2-client-secret-message">
                  The secret Front will use to sign requests to your server
                </Trans>
              </StyledMessageWrapperDiv>
            }
          />
        }
      />
      <CreateFormInputDisplay
        isRequired
        title={<Trans id="developers-application-server-modify-oauth2-scope-label">Scope</Trans>}
        input={
          <StyledOriginTextBox
            value={authConfig.scope ?? ''}
            onChange={(value) =>
              onAuthConfigChange({
                ...authConfig,
                scope: value,
              })
            }
            isRequired
            noMargin
            message={
              <StyledMessageWrapperDiv $isInDialog={isInDialog}>
                <Trans id="developers-application-server-modify-oauth2-scope-message">
                  The scope of the access request
                </Trans>
              </StyledMessageWrapperDiv>
            }
          />
        }
      />
      <CreateFormInputDisplay
        isRequired
        title={
          <Trans id="developers-application-server-modify-oauth2-auth-url-label">Authorization URL</Trans>
        }
        input={
          <StyledOriginTextBox
            value={authConfig.authorizationUrl ?? ''}
            onChange={(value) =>
              onAuthConfigChange({
                ...authConfig,
                authorizationUrl: value,
              })
            }
            isRequired
            noMargin
            hasError={
              typeof authConfig.authorizationUrl !== 'undefined' &&
              authConfig.authorizationUrl !== '' &&
              !isUrlValid(authConfig.authorizationUrl)
            }
            errorMessage={
              <StyledMessageWrapperDiv $isInDialog={isInDialog}>
                <Trans id="developers-application-server-modify-oauth2-auth-url-error-message">
                  Invalid url.
                </Trans>
              </StyledMessageWrapperDiv>
            }
            message={
              <StyledMessageWrapperDiv $isInDialog={isInDialog}>
                <Trans id="developers-application-server-modify-oauth2-auth-url-message">
                  The URL where Front will send users to begin the authentication process.
                </Trans>
              </StyledMessageWrapperDiv>
            }
          />
        }
      />
      <CreateFormInputDisplay
        isRequired
        title={<Trans id="developers-application-server-modify-oauth2-token-url-label">Token URL</Trans>}
        input={
          <StyledOriginTextBox
            value={authConfig.tokenUrl ?? ''}
            onChange={(value) =>
              onAuthConfigChange({
                ...authConfig,
                tokenUrl: value,
              })
            }
            isRequired
            noMargin
            hasError={
              typeof authConfig.tokenUrl !== 'undefined' &&
              authConfig.tokenUrl !== '' &&
              !isUrlValid(authConfig.tokenUrl)
            }
            errorMessage={
              <StyledMessageWrapperDiv $isInDialog={isInDialog}>
                <Trans id="developers-application-server-modify-oauth2-token-url-error-message">
                  Invalid url.
                </Trans>
              </StyledMessageWrapperDiv>
            }
            message={
              <StyledMessageWrapperDiv $isInDialog={isInDialog}>
                <Trans id="developers-application-server-modify-oauth2-token-url-message">
                  The URL Front should use to retrieve an access token
                </Trans>
              </StyledMessageWrapperDiv>
            }
          />
        }
      />
    </>
  );
}
